import React, { useEffect } from "react";
import { setSentryIdentity } from "@lib/sentry/identity";
import { AppProps } from "next/app";
import "tailwindcss/tailwind.css";
import "@styles/global.scss";
import "rc-tooltip/assets/bootstrap_white.css";
import DomainWrapper from "@components/layouts/domainWrapper";
import { DomainProvider } from "@hooks/useDomainContext";
import { FormProvider } from "@hooks/useFormContext";
import { ModalProvider } from "@hooks/useModalContext";
import { SessionProvider } from "@hooks/useSessionContext";
import { InsuranceProvider } from "@hooks/useInsuranceContext";
import { WarrantyProvider } from "@hooks/useWarrantyContext";
import { PageProps } from "@services/initialCallsTypes";
import NextErrorComponent from "next/error";
import OutBrain from "@components/shared/outBrain";
import {
    reportWebVitals as _reportWebVitals,
    setCaptureWebVitals,
    setRumMeta,
} from "src/utils/rum";
import type { NextWebVitalsMetric } from "next/app";

export function reportWebVitals(metric: NextWebVitalsMetric): void {
    if (typeof window === "undefined" || !window.__captureWebVitals) return;
    _reportWebVitals(metric);
}

export interface CustomAppProps extends AppProps<PageProps> {
    pageProps: PageProps;
}

function MyApp({ Component, pageProps }: CustomAppProps): JSX.Element {
    useEffect(() => {
        setSentryIdentity(
            pageProps.visitDetails?.visitId,
            pageProps.domain?.name,
            pageProps.nodeMark,
        );
    }, [
        pageProps.visitDetails?.visitId,
        pageProps.domain?.name,
        pageProps.nodeMark,
    ]);

    if (pageProps.error) {
        return <NextErrorComponent statusCode={pageProps.error.statusCode} />;
    }

    if (
        typeof window !== "undefined" &&
        pageProps?.domain &&
        pageProps?.visitDetails
    ) {
        const captureWebVitals = Boolean(
            pageProps.domain.config?.captureWebVitals,
        );
        setCaptureWebVitals(captureWebVitals);
        if (captureWebVitals) {
            setRumMeta({
                domainId: pageProps.domain.id,
                domainName: pageProps.domain.name,
                visitId: pageProps.visitDetails.visitId,
                visitorId: pageProps.visitDetails.visitorId,
                trafficSourceNetwork:
                    pageProps.visitDetails?.trafficSourceNetwork ?? "unknown",
            });
        }
    }

    return (
        <DomainProvider {...pageProps}>
            <FormProvider>
                <ModalProvider>
                    <SessionProvider>
                        <InsuranceProvider>
                            <WarrantyProvider>
                                <div id="domain-wrapper">
                                    <DomainWrapper
                                        domain={pageProps.domain}
                                        pageTitle={pageProps.pageTitle}
                                    >
                                        <OutBrain />
                                        <Component {...pageProps} />
                                    </DomainWrapper>
                                </div>
                            </WarrantyProvider>
                        </InsuranceProvider>
                    </SessionProvider>
                </ModalProvider>
            </FormProvider>
        </DomainProvider>
    );
}

export default MyApp;
